import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Plus, X } from "lucide-react";

let tagCache = null;
const loadTags = async (force = false) => { if (!tagCache || force) tagCache = (await api.get("/tags")).data.tags; return tagCache; };

// Tag chips (multi-select from the user's library, inline creation) + lesson note with debounced autosave.
export default function JournalEditor({ positionId, tags: initialTags = [], note: initialNote = "", onSaved, compact = false }) {
  const [library, setLibrary] = useState(tagCache || []);
  const [tags, setTags] = useState(initialTags);
  const [note, setNote] = useState(initialNote || "");
  const [adding, setAdding] = useState(false);
  const [saved, setSaved] = useState(null);
  const timer = useRef(null);
  const first = useRef(true);

  useEffect(() => { loadTags().then(setLibrary); }, []);
  useEffect(() => { setTags(initialTags); setNote(initialNote || ""); first.current = true; }, [positionId]); // eslint-disable-line

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const { data } = await api.patch(`/positions/${positionId}/journal`, { tags, note }); setSaved(Date.now()); onSaved?.(data.position); } catch { setSaved(false); }
    }, 700);
    return () => clearTimeout(timer.current);
  }, [tags, note]); // eslint-disable-line

  const toggle = (name) => setTags((t) => (t.includes(name) ? t.filter((x) => x !== name) : [...t, name]));
  const create = async (name) => {
    const n = name.trim(); if (!n) return setAdding(false);
    const { data } = await api.post("/tags", { name: n });
    tagCache = null; const lib = await loadTags(true); setLibrary(lib);
    if (!tags.includes(data.tag.name)) setTags((t) => [...t, data.tag.name]);
    setAdding(false);
  };
  const color = (name) => library.find((t) => t.name === name)?.color || "#7C879E";

  return (
    <div className="space-y-2" data-testid={`journal-editor-${positionId}`}>
      <div className="flex flex-wrap gap-1 items-center">
        {library.map((t) => {
          const on = tags.includes(t.name);
          return (
            <button key={t.id} data-testid={`journal-tag-${t.name.replace(/\s+/g, "-")}`} onClick={() => toggle(t.name)}
              className={`px-2 py-0.5 rounded-md border text-[11px] transition-colors ${on ? "text-[#0B0E14] font-semibold" : "text-muted-foreground hover:text-foreground"}`}
              style={on ? { background: t.color, borderColor: t.color } : { borderColor: `${t.color}66` }}>
              {t.name}
            </button>
          );
        })}
        {adding ? (
          <input autoFocus data-testid="journal-tag-new-input" placeholder="New tag…" onKeyDown={(e) => { if (e.key === "Enter") create(e.currentTarget.value); if (e.key === "Escape") setAdding(false); }} onBlur={(e) => create(e.target.value)}
            className="h-6 w-28 px-1.5 rounded border border-border bg-background text-[11px]" />
        ) : (
          <button data-testid="journal-tag-new" onClick={() => setAdding(true)} title="Create tag" className="h-6 w-6 rounded border border-dashed border-border flex items-center justify-center text-muted-foreground hover:text-primary"><Plus className="h-3 w-3" /></button>
        )}
      </div>
      {tags.length > 0 && <div className="flex flex-wrap gap-1 text-[10px]" data-testid="journal-selected-tags">{tags.map((t) => <span key={t} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${color(t)}22`, color: color(t) }}>{t}<button onClick={() => toggle(t)}><X className="h-2.5 w-2.5" /></button></span>)}</div>}
      <textarea data-testid="journal-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Lesson · why was this trade taken? What would you do differently?" rows={compact ? 2 : 3}
        className="w-full px-2 py-1.5 rounded-md border border-border bg-background text-xs resize-y" />
      <div className="text-[10px] text-muted-foreground text-right" data-testid="journal-save-state">{saved === false ? "save failed" : saved ? "saved" : ""}</div>
    </div>
  );
}
